import { createInstance, dateReviver } from '@/api';

import {
    Paging,
    PublicationClient,
    PublicationFilter,
    SearchResultOfPublicationFilterAndPublicationSearchResult,
    Sorting
} from '@/api/frontoffice/clients';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { i18n } from '@/i18n';
import { deepFreeze } from '@/helpers';

export interface PublicationListOptions {
    filter?: PublicationFilter;
    sorting?: Sorting;
    paging?: Paging;
}

export interface PublicationListStoreState {
    loading: boolean;
    publications?: SearchResultOfPublicationFilterAndPublicationSearchResult;
    filter?: PublicationFilter;
    sorting?: Sorting;
    paging?: Paging;
}

export const client = new PublicationClient('', createInstance(), dateReviver);

const defaultFilter: PublicationFilter = {
    name: null,
    organisationId: null,
    status: null,
    type: null,
    productGroupLabel: null,
    onlyActive: null
};

const defaultSorting: Sorting = {
    by: 'productLabel',
    descending: false
};

const defaultPaging: Paging = {
    pageNumber: 1,
    pageSize: 10
};

const defaultOptions = {
    filter: defaultFilter,
    sorting: defaultSorting,
    paging: defaultPaging
};

export const usePublicationListStore = defineStore('publications-publications-list-store', {
    state: (): PublicationListStoreState => ({
        loading: false,
        publications: null,
        filter: defaultFilter,
        sorting: defaultSorting,
        paging: defaultPaging
    }),
    getters: {
        metaData(): {
            totalRows: number;
            resultsPerPage: number;
            currentPage: number | undefined;
            serverside: boolean;
        } {
            return {
                totalRows: this.publications?.totalcount ?? 0,
                resultsPerPage: this.paging.pageSize,
                currentPage: this.paging.pageNumber,
                serverside: true
            };
        },
        defaultOptions(): PublicationListOptions {
            return defaultOptions;
        },
        options(): PublicationListOptions {
            return {
                filter: this.filter,
                sorting: this.sorting,
                paging: this.paging
            };
        }
    },
    actions: {
        clear() {
            this.publications = null;
            this.sorting = { ...defaultSorting };
            this.paging = { ...defaultPaging };
            this.filter = { ...defaultFilter };
            this.loading = false;
            useAlertStore().clearAlert();
        },
        init(options?: PublicationListOptions) {
            this.sorting = options?.sorting ? { ...options.sorting } : { ...defaultSorting };
            this.paging = options?.paging ? { ...options.paging } : { ...defaultPaging };
            this.filter = options?.filter ? { ...options.filter } : { ...defaultFilter };
            this.publications = null;
        },
        resetPager() {
            this.paging.pageNumber = 1;
        },
        updatePage(pageNumber: number) {
            this.paging.pageNumber = pageNumber;
        },
        updateSorting(by: string, order: string) {
            this.sorting.by = by;
            this.sorting.descending = order !== 'Asc';
        },
        updateFilter(filter: Partial<PublicationFilter>) {
            this.filter.organisationId = filter.organisationId;
            this.filter.name = filter.name;
            this.filter.status = filter.status;
            this.filter.type = filter.type;
            this.filter.productGroupLabel = filter.productGroupLabel;
            this.filter.onlyActive = filter.onlyActive;
        },
        async getLatestPublications() {
            this.loading = true;
            useAlertStore().clearAlert();
            try {
                const response = await client.getLatestPublications(
                    this.filter?.organisationId,
                    this.filter?.name,
                    this.filter?.type,
                    this.filter?.productGroupLabel,
                    this.filter?.status,
                    this.filter?.onlyActive,
                    mapToQueryableOrder(this.sorting?.by),
                    this.sorting?.descending,
                    this.paging?.pageNumber,
                    this.paging?.pageSize
                );
                this.publications = response;
            } catch (error) {
                console.log(error);
                useAlertStore().handleException(i18n.global.t('publications.list.error.title'), error);
            } finally {
                this.loading = false;
            }
        }
    }
});

function mapToQueryableOrder(order: string) {
    // tweak if necesary
    switch (order) {
        default:
            return order;
    }
}
